/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useState } from "react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { PauseCircle, Play, Loader2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { AttachPicker } from "../../pages/design/v3/PauseAttachments";

/**
 * 已签约客户详情 › 暂停 / 恢复设计（2026-10-15 新增）。
 *
 * 需求来源：客户处于设计阶段时（如业主资金未到位、等市政政策、业主出国），
 *   过去必须由设计部经理在设计部提申请、再等销售总监 / CEO 审批，销售侧无法自助处理。
 *   现在销售主管 / 设计主管 / admin / ceo 可直接在已签约客户详情页暂停设计。
 *
 * 与设计部「申请暂停」（pages/design/v3/PauseCancelDialog）的**唯一区别**：
 *   这里点击即生效，不再交销售总监 / CEO 审批。因此校验口径（原因 ≥2 字 + 至少 1 个凭证附件）
 *   保持一致 —— 后端 POST /design/v3/crm/{lid}/pause 同样强制校验，不是只在前端拦。
 *
 * 复用：附件上传 / 缩略图 / 体积格式化直接复用设计部的 AttachPicker（同一套分片上传，
 *   后端 _comment_files 以 db.files 为准重算 name/size/kind，防前端伪造）。
 */
export default function SignedDesignPauseDialog({ api: apiInjected, design, kind, onClose, onDone }) {
  // apiInjected 由调用方传入（CRM 详情页持有带鉴权的 axios 实例）；缺省回落到默认实例。
  const client = apiInjected || api;
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const isPause = kind === "pause";

  const run = async () => {
    if (isPause && reason.trim().length < 2) return toast.error("请填写原因（≥2 字）");
    if (isPause && files.length < 1) return toast.error("请上传至少 1 个凭证附件");
    setBusy(true);
    try {
      if (isPause) {
        await client.post(`/design/v3/crm/${design.leadId}/pause`, { reason: reason.trim(), files });
        toast.success("已暂停设计，设计部已收到通知");
      } else {
        await client.post(`/design/v3/crm/${design.leadId}/resume`, { note: reason.trim() });
        toast.success("已恢复设计");
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const Icon = isPause ? PauseCircle : Play;
  const color = isPause ? "text-amber-700" : "text-emerald-700";
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={`signed-design-${kind}-dialog`}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className={`font-bold flex items-center gap-2 ${color}`}><Icon className="h-5 w-5" />{isPause ? "暂停设计" : "恢复设计"}</h3>
        <p className="text-sm text-slate-700"><b>{design.address}</b>{design.designProjectNo ? <span className="text-slate-400"> · {design.designProjectNo}</span> : null}</p>
        <ul className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1" data-testid={`signed-design-${kind}-effects`}>
          {isPause ? <>
            <li className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />{`点击即生效，无需销售总监 / CEO 审批；项目转入「暂停」，不再计入进行中 / 逾期 / 停滞统计`}</li>
            <li>{`· 暂停期间数据、文件全部保留；销售主管 / 设计主管 / admin / CEO 可随时恢复`}</li>
            <li>{`· 需上传凭证（业主资金证明 / 市政政策文件 / 与业主沟通记录等）作为依据`}</li>
          </> : <>
            <li className="flex items-start gap-1"><Play className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" />{`立即恢复为暂停前的工作状态与目标日期，重新计入进行中统计`}</li>
            <li>{`· 设计部经理会收到恢复通知`}</li>
          </>}
        </ul>
        <label className="block text-xs text-slate-600">{isPause ? "原因 *" : "恢复说明（可选）"}
          <textarea data-testid={`signed-design-${kind}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
            placeholder={isPause ? "如：业主资金暂未到位 / 等待市政政策 / 业主出国…" : "如：业主资金到位，恢复设计"} />
        </label>
        {isPause && (
          <div className="space-y-1.5">
            <p className="text-xs text-slate-600">凭证附件<b className="text-red-500"> *</b><span className="text-slate-400">{" · 图片 / PDF / 视频 / 图纸 / 文档等"}</span></p>
            <AttachPicker api={client} files={files} setFiles={setFiles} busy={busy} testid={`signed-design-${kind}-attach`} />
          </div>
        )}
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
          <Button size="sm" variant="outline" data-testid={`signed-design-${kind}-cancel`} onClick={onClose}>取消</Button>
          <Button size="sm" data-testid={`signed-design-${kind}-confirm`} disabled={busy || (isPause && (reason.trim().length < 2 || files.length < 1))} onClick={run}
            className={isPause ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isPause ? "确认暂停" : "确认恢复")}
          </Button>
        </div>
      </div>
    </div>
  );
}